import { useState } from "react";
import {
  Power, KeyRound, ScrollText, ShieldCheck, Trash2, Download, Laptop, Smartphone,
} from "lucide-react";
import { PageHeader, Panel } from "@/components/dashboard/primitives";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const initialTokens = [
  { id: "tk-1", name: "Production key", masked: "vora_live_••••••7Q2", created: "Mar 2026" },
  { id: "tk-2", name: "Sandbox key", masked: "vora_test_••••••4X9", created: "Apr 2026" },
];

const sessions = [
  { id: "s1", device: "MacBook Pro · Chrome", icon: Laptop, where: "San Francisco · current", current: true },
  { id: "s2", device: "iPhone 15 · Safari", icon: Smartphone, where: "San Francisco · 2h ago", current: false },
];

const principles = [
  { icon: KeyRound, title: "Least-privilege access", desc: "Each agent only gets the permissions it needs." },
  { icon: ShieldCheck, title: "Human approval", desc: "Sensitive transactions always route to a human." },
  { icon: ScrollText, title: "Transparent history", desc: "Every transaction and policy decision is logged." },
];

export default function SecurityCenter() {
  const [tokens, setTokens] = useState(initialTokens);

  const revoke = (id) => {
    setTokens((prev) => prev.filter((t) => t.id !== id));
    toast.success("API token revoked (demo)");
  };

  return (
    <div>
      <PageHeader title="Security" subtitle="Hardware controls, token management and the global kill-switch." />

      {/* Kill switch */}
      <Panel className="p-6 border-red-500/20 mb-5" data-testid="global-killswitch-panel">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-start gap-4">
            <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-red-500/12 text-red-400"><Power className="h-6 w-6" /></span>
            <div>
              <h3 className="text-base font-semibold text-white">Global emergency stop</h3>
              <p className="text-sm text-slate-400 mt-1 max-w-md">Instantly pause every agent, freeze all wallets and disable payments across the workspace.</p>
            </div>
          </div>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button className="bg-red-500 hover:bg-red-600 text-white rounded-full gap-2" data-testid="global-killswitch-trigger">
                <Power className="h-4 w-4" /> Activate stop
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-[#0B1526] border-white/10 text-white">
              <AlertDialogHeader>
                <AlertDialogTitle>Activate global emergency stop?</AlertDialogTitle>
                <AlertDialogDescription className="text-slate-400">This halts all agent activity immediately. Demo action in the sandbox.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="bg-transparent border-white/15 text-white hover:bg-white/5">Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={() => toast.success("Global stop activated (demo)")} className="bg-red-500 hover:bg-red-600 text-white" data-testid="global-killswitch-confirm">Stop everything</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </Panel>

      <div className="grid lg:grid-cols-2 gap-5">
        {/* API tokens */}
        <Panel className="p-6" data-testid="api-tokens-panel">
          <h3 className="text-base font-semibold text-white mb-4">API tokens</h3>
          <div className="space-y-3">
            {tokens.length === 0 && <p className="text-sm text-slate-500">No active tokens.</p>}
            {tokens.map((t) => (
              <div key={t.id} className="flex items-center justify-between rounded-xl border border-white/8 bg-white/[0.02] px-4 py-3">
                <div>
                  <p className="text-sm text-white">{t.name}</p>
                  <p className="text-xs font-mono text-slate-500 mt-0.5">{t.masked} · {t.created}</p>
                </div>
                <Button size="sm" variant="outline" onClick={() => revoke(t.id)} className="bg-transparent border-red-500/25 text-red-400 hover:bg-red-500/10 rounded-full gap-1.5" data-testid={`revoke-token-${t.id}`}>
                  <Trash2 className="h-3.5 w-3.5" /> Revoke
                </Button>
              </div>
            ))}
          </div>
          <p className="mt-4 text-xs text-slate-500">Secrets are never shown in full. Create and manage keys from Settings → API.</p>
        </Panel>

        {/* Sessions */}
        <Panel className="p-6" data-testid="sessions-panel">
          <h3 className="text-base font-semibold text-white mb-4">Active sessions</h3>
          <div className="space-y-3">
            {sessions.map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.id} className="flex items-center justify-between rounded-xl border border-white/8 bg-white/[0.02] px-4 py-3">
                  <div className="flex items-center gap-3">
                    <Icon className="h-5 w-5 text-slate-400" />
                    <div>
                      <p className="text-sm text-white">{s.device}</p>
                      <p className="text-xs text-slate-500">{s.where}</p>
                    </div>
                  </div>
                  {s.current ? (
                    <span className="text-xs text-emerald-400">This device</span>
                  ) : (
                    <Button size="sm" variant="ghost" onClick={() => toast.success("Session revoked (demo)")} className="text-slate-300 hover:text-white hover:bg-white/5 rounded-full">Sign out</Button>
                  )}
                </div>
              );
            })}
          </div>
        </Panel>
      </div>

      {/* Audit + principles */}
      <Panel className="mt-5 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-white">Audit log</h3>
          <Button size="sm" variant="outline" onClick={() => toast.success("Audit log export started (demo)")} className="bg-transparent border-white/12 text-white hover:bg-white/5 rounded-full gap-1.5" data-testid="export-audit-btn">
            <Download className="h-3.5 w-3.5" /> Export
          </Button>
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          {principles.map((p) => {
            const Icon = p.icon;
            return (
              <div key={p.title} className="rounded-xl border border-white/8 bg-white/[0.02] p-4">
                <Icon className="h-5 w-5 text-[#4F8CFF]" />
                <p className="mt-3 text-sm font-medium text-white">{p.title}</p>
                <p className="mt-1 text-xs text-slate-400">{p.desc}</p>
              </div>
            );
          })}
        </div>
      </Panel>
    </div>
  );
}
